import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiChevronLeft, FiChevronRight, FiMonitor } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { ROLE_LABELS } from '../../utils/roles';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'web_management', label: 'Web Management Staff' },
  { value: 'store_management', label: 'Store Management Staff' },
];

const inputClass = 'px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';

const AdminLoginHistory = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (role) params.role = role;
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await userAPI.getLoginHistory(params);
      setEntries(res.data?.data?.entries || []);
      setTotalPages(res.data?.data?.totalPages || 1);
      setTotal(res.data?.data?.total || 0);
    } catch {
      toast.error('Failed to load login history.');
    } finally {
      setLoading(false);
    }
  }, [page, role, from, to]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    setPage(1);
  }, [role, from, to]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#1e3a8a]">Login Activity</h2>
        <p className="text-slate-500 text-sm mt-1">See which staff account logged in, from where, and when</p>
      </div>

      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-4 items-center flex-wrap shadow-sm">
        <select value={role} onChange={(e) => setRole(e.target.value)} className={inputClass}>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
        </div>
        <div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
        </div>
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); }}
            className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
            Clear dates
          </button>
        )}
        <div className="text-sm text-slate-400 flex-shrink-0 ml-auto">
          {total} login{total !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Role</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Login Time</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">IP Address</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Device</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <motion.tr key={entry._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                    <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{entry.name}</td>
                    <td className="px-5 py-4 text-slate-500">{entry.email}</td>
                    <td className="px-5 py-4">
                      <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">
                        {ROLE_LABELS[entry.role] || entry.role}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{formatDate(entry.loginAt, 'MMM dd, yyyy h:mm a')}</td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-400 text-xs">{entry.ipAddress || '—'}</td>
                    <td className="px-5 py-4 hidden lg:table-cell text-slate-400 text-xs max-w-[220px]">
                      <span className="truncate flex items-center gap-1.5">
                        <FiMonitor className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{entry.userAgent || '—'}</span>
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {entries.length === 0 && (
            <div className="text-center py-16">
              <FiClock className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No login activity yet</p>
              <p className="text-slate-400 text-sm mt-1">Logins from staff accounts will show up here.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                <FiChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminLoginHistory;
